import type {
    EnvironmentalChartPoint,
    EnvironmentalMetric,
    EnvironmentalMetricId,
    EnvironmentalNotification,
    EnvironmentalStressMonitoringData,
    EnvironmentalStressMonitoringWindow,
} from '../../constants/environmentalStressMonitoring.model';

export type EnvironmentalStressMonitoringRequest = {
    deviceId: string;
    window: EnvironmentalStressMonitoringWindow;
    organizationId?: string;
    parameter?: string;
    timeWindow?: number;
    page?: number;
    size?: number;
};

export type EnvironmentalStressMonitoringFilterRequest = Omit<
    EnvironmentalStressMonitoringRequest,
    'window' | 'parameter'
> & {
    window?: EnvironmentalStressMonitoringWindow;
    parameter?: string | string[];
    parameters?: string[];
    sort?: { key: 'insightsdatetime'; sortType: 'ASC' | 'DESC' } | null;
    severity?: 'ALARM' | 'WARNING';
    useOpenDateTimeWindow?: boolean;
};

export type EnvironmentalMetricResponse = {
    displayName: string;
    parameter: string;
    warning: number;
    alarm: number;
    avg?: number;
    unit: string;
    icon?: EnvironmentalStressMonitoringIcon | string;
};

export type EnvironmentalStressMonitoringIcon = {
    web?: {
        name?: string;
        family?: string;
        style?: { color?: string; backgroundColor?: string };
    };
    mobile?: { name?: string; family?: string; style?: { color?: string; backgroundColor?: string } };
};

export type EnvironmentalMetricsResponse = {
    success: boolean;
    data: {
        parameters: EnvironmentalMetricResponse[];
        total: { warning: number; alarm: number };
    };
    requestId?: string;
};

export type EnvironmentalFilterRecord = {
    id?: string;
    icon?: EnvironmentalStressMonitoringIcon;
    icons?: { status?: { indicator?: string; icon?: EnvironmentalStressMonitoringIcon } };
    parameter: string;
    value: number;
    threshold: number;
    thresholdUnit: string;
    severity: string;
    insightsdatetime: string | number;
    displayName: string;
    unit: string;
    window: string;
    openDate: number;
};

export type EnvironmentalFilterResponse = {
    success: boolean;
    data: { total: number; records: EnvironmentalFilterRecord[] };
    requestId?: string;
};

export type EnvironmentalStressMonitoringDetailsResponse = {
    success: boolean;
    data: EnvironmentalFilterRecord & {
        deviceId: string;
        organizationId: string;
    };
    requestId?: string;
};

type EnvironmentalWidgetStatus = 'alarm' | 'warning' | 'normal';

export type EnvironmentalStressMonitoringWidgetData = {
    overview: {
        title: string;
        status: {
            label: string;
            severity: EnvironmentalWidgetStatus;
            icon?: EnvironmentalStressMonitoringIcon;
        };
        metrics: Array<{
            name: string;
            value: string;
            unit: string;
            status: EnvironmentalWidgetStatus;
            displayType: 'AVG';
            icon?: EnvironmentalStressMonitoringIcon;
        }>;
    };
    secondary: any[];
    total: number;
    statusCount: { alarms: number; warnings: number };
};

/* eslint-disable @typescript-eslint/naming-convention */
export type EnvironmentalSearchRecord = {
    consecutive_breaches?: number | null;
    device_id: string;
    display_name: string;
    dq_flag: boolean;
    epoch: number;
    notification_id: string;
    parameter: string;
    serial_number: string;
    severity: string;
    sid?: number | null;
    status?: string | null;
    threshold_alarm: number | null;
    threshold_direction?: string | null;
    threshold_unit: string;
    threshold_warning: number | null;
    timestamp: string | null;
    unit: string;
    value: number;
    window: string;
};

export type EnvironmentalSearchResponse = {
    count?: number;
    data: EnvironmentalSearchRecord[];
    next_page_id?: string;
};
/* eslint-enable @typescript-eslint/naming-convention */

/* eslint-disable @typescript-eslint/naming-convention */
const metricIds: Record<string, EnvironmentalMetricId> = {
    temperature: 'temperature',
    relative_humidity: 'humidity',
    humidity: 'humidity',
    emc: 'emc',
    dew_point: 'dew-point',
    approaching_dew_point: 'dew-point-margin',
};
/* eslint-enable @typescript-eslint/naming-convention */

const getMetricId = (parameter: string): EnvironmentalMetricId | undefined => metricIds[parameter];

const getSeverity = (value: string): EnvironmentalNotification['severity'] =>
    value.toLowerCase() === 'alarm' ? 'alarm' : 'warning';

const getKnownSeverity = (value?: string): 'alarm' | 'warning' | undefined => {
    const severity = value?.toLowerCase();
    return severity === 'alarm' || severity === 'warning' ? severity : undefined;
};

const indexRecordsByParameter = <T extends { parameter: string }>(records: T[]): Map<string, T> =>
    new Map(records.map((record) => [record.parameter, record]));

const monitoringWindows: Record<string, EnvironmentalStressMonitoringWindow> = { DAILY: 'DAILY', MONTHLY: 'MONTHLY' };

const getMonitoringWindow = (window: unknown): EnvironmentalStressMonitoringWindow | undefined =>
    typeof window === 'string' ? monitoringWindows[window.toUpperCase()] : undefined;

const getEpochMilliseconds = (epoch: number): number | undefined => {
    const milliseconds = Math.abs(epoch) < 1e12 ? epoch * 1000 : epoch;
    return Number.isFinite(epoch) && Number.isFinite(new Date(milliseconds).getTime()) ? milliseconds : undefined;
};

const getRecordCounts = (records: EnvironmentalFilterRecord[]): { warning: number; alarm: number } =>
    records.reduce(
        (counts, record) => {
            const severity = record.severity.toLowerCase();
            if (severity === 'alarm') counts.alarm += 1;
            if (severity === 'warning') counts.warning += 1;
            return counts;
        },
        { warning: 0, alarm: 0 }
    );

const getRecordSeverity = (
    filterRecord?: EnvironmentalFilterRecord,
    searchRecord?: EnvironmentalSearchRecord
): 'alarm' | 'warning' =>
    getKnownSeverity(filterRecord?.severity) ??
    getKnownSeverity(searchRecord?.severity) ??
    getKnownSeverity(searchRecord?.status ?? undefined) ??
    (searchRecord && searchRecord.threshold_alarm !== null && searchRecord.value >= searchRecord.threshold_alarm
        ? 'alarm'
        : 'warning');

const getRecordLabel = (parameter: string, record?: EnvironmentalFilterRecord): string =>
    record?.displayName?.toUpperCase() ?? parameter.toUpperCase();

const getRecordThresholds = (
    filterRecord?: EnvironmentalFilterRecord,
    searchRecord?: EnvironmentalSearchRecord
): Pick<EnvironmentalMetric, 'warningThreshold' | 'alarmThreshold'> => ({
    warningThreshold:
        searchRecord?.threshold_warning ??
        (filterRecord?.severity?.toLowerCase() === 'warning' ? filterRecord.threshold : 0),
    alarmThreshold:
        searchRecord?.threshold_alarm ??
        (filterRecord?.severity?.toLowerCase() === 'alarm' ? filterRecord.threshold : 0),
});

const getRecordValue = (filterRecord?: EnvironmentalFilterRecord, searchRecord?: EnvironmentalSearchRecord): string =>
    String(filterRecord?.value ?? searchRecord?.value ?? 0);

const getRecordUnit = (filterRecord?: EnvironmentalFilterRecord, searchRecord?: EnvironmentalSearchRecord): string =>
    filterRecord?.unit ?? searchRecord?.unit ?? '';

const toRecordMetric = (
    parameter: string,
    filterRecord?: EnvironmentalFilterRecord,
    searchRecord?: EnvironmentalSearchRecord
): EnvironmentalMetric => {
    const severity = getRecordSeverity(filterRecord, searchRecord);
    const thresholds = getRecordThresholds(filterRecord, searchRecord);
    return {
        id: getMetricId(parameter) ?? 'temperature',
        parameter,
        label: getRecordLabel(parameter, filterRecord),
        value: getRecordValue(filterRecord, searchRecord),
        unit: getRecordUnit(filterRecord, searchRecord),
        status: severity,
        statusLabel: `In ${severity === 'alarm' ? 'Alarm' : 'Warning'} Range`,
        ...thresholds,
    };
};

const createMetricsFromRecords = (
    filterRecords: EnvironmentalFilterRecord[],
    searchRecords: EnvironmentalSearchRecord[],
    filterRecordsByParameter: Map<string, EnvironmentalFilterRecord>,
    searchRecordsByParameter: Map<string, EnvironmentalSearchRecord>
): EnvironmentalMetric[] => {
    const parameters = new Set([
        ...filterRecords.map((record) => record.parameter),
        ...searchRecords.map((record) => record.parameter),
    ]);
    return Array.from(parameters).map((parameter) =>
        toRecordMetric(parameter, filterRecordsByParameter.get(parameter), searchRecordsByParameter.get(parameter))
    );
};

const formatDate = (timestamp: string | number): { date: string; time: string } => {
    const date = new Date(timestamp);
    return {
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    };
};

const formatChartDate = (timestamp: string | number): { date: string; time: string } => {
    const date = new Date(timestamp);
    return {
        date: date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }),
        time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/\s+([AP]M)$/i, '$1'),
    };
};

const toMetric = (
    item: EnvironmentalMetricResponse,
    filterRecord?: EnvironmentalFilterRecord,
    searchRecord?: EnvironmentalSearchRecord
): EnvironmentalMetric => {
    const id = getMetricId(item.parameter) ?? 'temperature';
    const status = item.alarm > 0 ? 'alarm' : item.warning > 0 ? 'warning' : 'expected';
    const count = status === 'alarm' ? item.alarm : item.warning;
    return {
        id,
        parameter: item.parameter,
        label: item.displayName.toUpperCase(),
        value: String(item.avg ?? count),
        unit: item.unit,
        status,
        statusLabel:
            status === 'expected'
                ? 'In Expected Range'
                : `${count} in ${status === 'alarm' ? 'Alarm' : 'Warning'} Range`,
        icon: toEnvironmentalStressMonitoringIcon(item.icon),
        ...getRecordThresholds(filterRecord, searchRecord),
    };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const toEnvironmentalStressMonitoringIcon = (
    icon: EnvironmentalMetricResponse['icon']
): EnvironmentalStressMonitoringIcon | undefined => {
    if (!icon) return undefined;
    if (typeof icon !== 'string') return icon;

    try {
        const parsed: unknown = JSON.parse(icon);
        if (!isRecord(parsed)) return undefined;
        const toPlatformIcon = (value: unknown): EnvironmentalStressMonitoringIcon['web'] | undefined => {
            if (!isRecord(value)) return undefined;
            const style = isRecord(value.style)
                ? {
                      ...(typeof value.style.color === 'string' ? { color: value.style.color } : {}),
                      ...(typeof value.style.backgroundColor === 'string'
                          ? { backgroundColor: value.style.backgroundColor }
                          : {}),
                  }
                : undefined;
            return {
                ...(typeof value.name === 'string' ? { name: value.name } : {}),
                ...(typeof value.family === 'string' ? { family: value.family } : {}),
                ...(style ? { style } : {}),
            };
        };
        const web = toPlatformIcon(parsed.web);
        const mobile = toPlatformIcon(parsed.mobile);
        return web || mobile ? { ...(web ? { web } : {}), ...(mobile ? { mobile } : {}) } : undefined;
    } catch {
        return undefined;
    }
};

const toNotification = (item: EnvironmentalFilterRecord, index: number): EnvironmentalNotification => {
    const occurredAt = item.insightsdatetime || item.openDate;
    const { date, time } = formatDate(occurredAt);
    return {
        id: item.id ?? `${item.parameter}-${item.openDate || index}`,
        occurredAt,
        parameter: item.parameter,
        window: getMonitoringWindow(item.window),
        metricId: getMetricId(item.parameter) ?? 'temperature',
        title: item.displayName,
        severity: getSeverity(item.severity),
        value: String(item.value),
        unit: item.unit,
        time,
        date,
        message: `Threshold: ${item.threshold} ${item.thresholdUnit}`,
        recommendation: `${item.displayName} exceeded its configured threshold.`,
        eventDetails: {
            average: `${item.value} ${item.unit}`,
            integrated: `Threshold: ${item.threshold} ${item.thresholdUnit}`,
            threshold: `Threshold: ${item.threshold} ${item.thresholdUnit}`,
        },
    };
};

const getChartTimestamp = (item: EnvironmentalSearchRecord): string | number | undefined => {
    const timestamp = getEpochMilliseconds(item.epoch) ?? item.timestamp;
    if (timestamp === null || !Number.isFinite(new Date(timestamp).getTime())) {
        // eslint-disable-next-line no-console
        console.warn('Skipping ESM chart record with invalid epoch and timestamp');
        return undefined;
    }
    return timestamp;
};

const toDailyPoint = (item: EnvironmentalSearchRecord, timestamp: string | number): EnvironmentalChartPoint => {
    const { date, time } = formatChartDate(timestamp);
    return toChartPoint(date, time, item.parameter, item.value);
};

const toChartPoint = (date: string, timestamp: string, parameter: string, value: number): EnvironmentalChartPoint => {
    const values = { temperature: 0, humidity: 0, emc: 0, dewPoint: 0, dewPointMargin: 0 };
    const metricId = getMetricId(parameter);
    if (metricId === 'temperature') values.temperature = value;
    if (metricId === 'humidity') values.humidity = value;
    if (metricId === 'emc') values.emc = value;
    if (metricId === 'dew-point') values.dewPoint = value;
    if (metricId === 'dew-point-margin') values.dewPointMargin = value;
    return { date, timestamp, ...values };
};

export const toEnvironmentalStressMonitoringData = (
    metricsResponse: EnvironmentalMetricsResponse | undefined,
    filterResponse?: EnvironmentalFilterResponse,
    searchResponse?: EnvironmentalSearchResponse
): EnvironmentalStressMonitoringData => {
    const metricData = metricsResponse?.data;
    const filterRecords = filterResponse?.data?.records ?? [];
    const searchRecords = searchResponse?.data ?? [];
    const filterRecordsByParameter = indexRecordsByParameter(filterRecords);
    const searchRecordsByParameter = indexRecordsByParameter(searchRecords);
    const metrics = metricData?.parameters?.length
        ? metricData.parameters.map((item) =>
              toMetric(item, filterRecordsByParameter.get(item.parameter), searchRecordsByParameter.get(item.parameter))
          )
        : createMetricsFromRecords(filterRecords, searchRecords, filterRecordsByParameter, searchRecordsByParameter);
    const chartRecords = searchRecords.flatMap((item) => {
        const timestamp = getChartTimestamp(item);
        return timestamp === undefined ? [] : [{ item, timestamp }];
    });
    const chartPoints = chartRecords.map(({ item, timestamp }) => toDailyPoint(item, timestamp));
    return {
        serialNumber: searchRecords[0]?.serial_number,
        metrics,
        notificationCounts: metricData?.total ?? getRecordCounts(filterRecords),
        notifications: filterRecords.map(toNotification),
        chartPoints,
        chartPointsByRange: {
            '15-minutes': chartPoints,
            '1-day': chartPoints,
            '1-week': chartPoints,
            '1-month': chartPoints,
            '3-months': chartPoints,
        },
        monthlyChartPoints: chartPoints.map((point, index) => ({
            month: point.date,
            monthLabel: new Date(chartRecords[index].timestamp).toLocaleDateString('en-US', {
                month: 'numeric',
                year: 'numeric',
            }),
            timestamp: point.timestamp,
            temperature: point.temperature,
            humidity: point.humidity,
            emc: point.emc,
            dewPoint: point.dewPoint,
            dewPointMargin: point.dewPointMargin,
        })),
        timeRanges: [
            { id: '15-minutes', label: '15 Minutes' },
            { id: '1-day', label: '1 Day' },
            { id: '1-week', label: '1 Week' },
            { id: '1-month', label: '1 Month' },
            { id: '3-months', label: '3 Months' },
        ],
        notificationHistory: filterRecords.map((item, index) => ({
            ...toNotification(item, index),
            severity: getSeverity(item.severity),
            average: `${item.value} ${item.unit}`,
            threshold: `${item.threshold} ${item.thresholdUnit}`,
        })),
        rules: {
            samplingIntervalMinutes: 15,
            notificationTriggerConsecutivePoints: 1,
            severityMapping: { warning: 'warning', alarm: 'alarm' },
        },
    };
};

const widgetMetricPresentation = Object.fromEntries<{ name: string; order: number }>([
    ['relative_humidity', { name: 'Remote Humidity', order: 0 }],
    ['humidity', { name: 'Remote Humidity', order: 0 }],
    ['temperature', { name: 'Remote Temperature', order: 1 }],
    ['emc', { name: 'EMC', order: 2 }],
    ['dew_point', { name: 'Dew Point', order: 3 }],
] as const);

export const toEnvironmentalStressMonitoringWidgetData = (
    metricsResponse?: EnvironmentalMetricsResponse,
    filterResponse?: EnvironmentalFilterResponse
): EnvironmentalStressMonitoringWidgetData => {
    const { metrics, notifications, notificationCounts } = toEnvironmentalStressMonitoringData(
        metricsResponse,
        filterResponse
    );
    const firstRecord = filterResponse?.data?.records?.[0];
    const severity = getKnownSeverity(firstRecord?.severity) ?? 'normal';
    const statusLabels = { alarm: 'Alarm', warning: 'Warning', normal: 'Normal' };

    return {
        overview: {
            title: 'WIDGETS:ESM_30_DAY_OVERVIEW',
            status: {
                label: statusLabels[severity],
                severity,
                icon: firstRecord?.icons?.status?.icon ?? firstRecord?.icon,
            },
            metrics: [...metrics]
                .sort(
                    (first, second) =>
                        (widgetMetricPresentation[first.parameter]?.order ?? Infinity) -
                        (widgetMetricPresentation[second.parameter]?.order ?? Infinity)
                )
                .map((metric) => ({
                    name: widgetMetricPresentation[metric.parameter]?.name ?? metric.label,
                    value: metric.value,
                    unit: metric.unit,
                    status: metric.status === 'expected' ? 'normal' : metric.status,
                    displayType: 'AVG',
                    icon: metric.icon,
                })),
        },
        secondary: notifications ?? [],
        total: filterResponse?.data?.total ?? notifications.length,
        statusCount: { alarms: notificationCounts.alarm, warnings: notificationCounts.warning },
    };
};
